import api from '../services/api';

/**
 * clinicalUtils — helpers compartilhados do módulo GRC.
 * UI em pt-BR; campos da API em inglês.
 */

export const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

// Histórico: até 2026-09-12, GOAL_TYPE_LABELS/COUNTER_TYPES eram um catálogo
// fixo (ENUM Postgres global). Substituído por um catálogo configurável POR
// EMPRESA na tabela `clinical_goal_types` (ver fetchGoalTypes/goalTypeLabel
// abaixo). Mantido só como nota — não use mais nos componentes.

export const SEVERITY_LABELS = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };

export function fmtBRL(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function fmtPct(ratio, digits = 1) {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return '0%';
  return `${(n * 100).toFixed(digits).replace('.', ',')}%`;
}

export function fmtDateBR(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

/** Extrai a mensagem de erro da API (padrão do error handler global). */
export function apiErrorMessage(err, fallback = 'Erro inesperado. Tente novamente.') {
  return (
    (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
    (err && err.message) ||
    fallback
  );
}

/**
 * Lista de unidades para os selects.
 * Rota real e confirmada do projeto: GET /companies (ver CLAUDE.md).
 */
export async function fetchCompanies() {
  const { data } = await api.get('/companies');
  return Array.isArray(data) ? data : data.data || [];
}

/**
 * Unidade inicial pra preencher o select "Unidade" de cada tela do módulo
 * GRC: prefere a "empresa ativa" escolhida no seletor do topo (CompanySwitcher,
 * `localStorage.activeCompanyId`) quando ela existe E está na lista recebida
 * — sem isso, toda tela abria sempre na primeira empresa da lista (ordem de
 * id), ignorando a empresa que o usuário já tinha selecionado no topo.
 * Cai pra `list[0].id` nos demais casos (nenhuma empresa ativa, ou "Todas as
 * empresas" selecionada no topo). `list` vazia retorna ''.
 */
export function preferredCompanyId(list) {
  if (!Array.isArray(list) || list.length === 0) return '';
  let activeId = null;
  try { activeId = localStorage.getItem('activeCompanyId'); } catch { /* ignore */ }
  const match = activeId && list.some((c) => String(c.id) === String(activeId));
  return String(match ? activeId : list[0].id);
}

/**
 * Catálogo de tipos de meta/contador da empresa (por empresa — ver
 * clinical_goal_types). `appliesTo` é opcional ('goal' | 'counter'); omitido
 * traz todo o catálogo ativo (goal + counter juntos), pra derivar os dois
 * subconjuntos em memória no componente.
 */
// `active`: true (padrão) traz só os tipos ativos — uso normal em telas que
// só oferecem tipos que podem ser escolhidos agora (dropdowns de meta/
// contador, dashboards). Passe `active: null` para trazer o catálogo
// completo (ativos + inativos) — necessário na tela de gerenciamento do
// catálogo, senão um tipo desativado nunca aparece pra poder ser reativado.
export async function fetchGoalTypes({ companyId, appliesTo, active = true } = {}) {
  const params = {};
  if (companyId !== undefined) params.company_id = companyId;
  if (appliesTo !== undefined) params.applies_to = appliesTo;
  if (active !== null) params.active = active;
  const { data } = await api.get('/clinical-results/goal-types', { params });
  return Array.isArray(data) ? data : data.data || [];
}

/** Label de um tipo pela key, com fallback pra própria key (dado histórico
 * cujo tipo foi removido/desativado não deve quebrar tabelas/exports). */
export function goalTypeLabel(list, key) {
  return (Array.isArray(list) ? list : []).find((t) => t.key === key)?.label || key;
}

/** Classe CSS da cor do semáforo — reutiliza o padrão de avaliações. */
export function semaforoClass(color) {
  if (color === 'verde') return 'semaforo-verde';
  if (color === 'amarelo') return 'semaforo-amarelo';
  if (color === 'vermelho') return 'semaforo-vermelho';
  return 'semaforo-neutro';
}
